import { useLazyQuery } from "@apollo/react-hooks";
import React, { useState, useEffect, Fragment } from "react";
import { DatePicker, Button, Table, Select, Tooltip } from "antd";
import { Column } from "@ant-design/plots";

import { GET_DASHBOARD_6_MONTHLY_APPROVED_PERMIT_COUNT } from "../graphql/modules";
import { warnNotify } from "../util";

const { RangePicker } = DatePicker;

// Pin the Grant Total summary row to the bottom of the scrollable body
// so it stays visible after scrolling through long month lists.
const SUMMARY_STICKY_CSS = `
.d6-summary-fixed .ant-table-summary {
    position: sticky;
    bottom: 0;
    z-index: 3;
}
.d6-summary-fixed .ant-table-summary > tr > td {
    background: #fafafa !important;
    font-weight: 600;
}
`;

export const DashboardV2D6 = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sites, setSites] = useState([]);

  const [GetMonthlyApprovedData, { data, loading, error }] = useLazyQuery(
    GET_DASHBOARD_6_MONTHLY_APPROVED_PERMIT_COUNT,
    {
      variables: { startDate, endDate, sites },
      fetchPolicy: "cache-and-network",
    },
  );

  useEffect(() => {
    if (error) {
      warnNotify(error.message);
    }
  }, [error]);

  const tableColumns = [
    { title: "Month", dataIndex: "month", key: "month" },
    { title: "General", dataIndex: "generalPermitCount", key: "generalPermitCount" },
    { title: "Tool Install", dataIndex: "toolInstallPermitCount", key: "toolInstallPermitCount" },
    { title: "Child Permit", dataIndex: "childPermitCount", key: "childPermitCount" },
    { title: "Grant Total", dataIndex: "grantTotal", key: "grantTotal" },
  ];

  const results = data?.GetMonthlyApprovedPermitCount?.results ?? [];

  // Pre-compute grand totals so the table summary row can render them.
  const grantTotals = results.reduce(
    (acc, row) => {
      acc.generalPermitCount += row.generalPermitCount || 0;
      acc.toolInstallPermitCount += row.toolInstallPermitCount || 0;
      acc.childPermitCount += row.childPermitCount || 0;
      acc.grantTotal += row.grantTotal || 0;
      return acc;
    },
    { generalPermitCount: 0, toolInstallPermitCount: 0, childPermitCount: 0, grantTotal: 0 },
  );

  const chartData = results.flatMap((row) => [
    { month: row.month, type: "General Permit", count: row.generalPermitCount },
    { month: row.month, type: "Tool Install Permit", count: row.toolInstallPermitCount },
    { month: row.month, type: "Child Permit", count: row.childPermitCount },
  ]);

  const chartConfig = {
    isGroup: true,
    xField: "month",
    yField: "count",
    seriesField: "type",
    color: ["#2E75B6", "#C55A11", "#548235"],
    style: { inset: 5 },
    legend: {
      position: "bottom",
      color: {
        position: "bottom",
      },
    },
    label: {
      position: "middle",
      style: {
        fill: "#000",
        fontSize: (datum, data) => {
          const monthCount = new Set(data.map((d) => d.month)).size;
          const groupSize = new Set(data.map((d) => d.type)).size || 1;
          const months = Math.round(monthCount / groupSize);
          if (months >= 18) return 8;
          if (months >= 12) return 9;
          if (months >= 6) return 10;
          return 12;
        },
        fontWeight: 500,
      },
      formatter: (datum) => (datum.count === 0 ? "" : `${datum.count}`),
    },
  };

  const handleDateFrom = (dates) => {
    if (dates && dates.length === 2) {
      const formattedDates = dates.map((d) => d.format("YYYY-MM-DD"));
      setStartDate(formattedDates[0]);
      setEndDate(formattedDates[1]);
    } else {
      setStartDate("");
      setEndDate("");
    }
  };

  const handleSiteChange = (value) => {
    setSites(value);
  };

  const handleSubmit = () => {
    if (startDate === "" || endDate === "") {
      warnNotify("Please select a date range");
      return;
    }
    if (sites.length === 0) {
      warnNotify("Please select at least one site");
      return;
    }
    GetMonthlyApprovedData();
  };

  return (
    <Fragment>
      <div className="row dec-row">
        <div className="card">
          <div className="col-12 col-md-12">
            <div className="col-12 col-md-12 text-left">
              <h4 className="text-left">Monthly Approved Permit Count</h4>
            </div>
            <div className="row dec-row">
              <div className="col-3 col-md-3">
                <RangePicker
                  style={{ width: "100%" }}
                  id={{
                    start: "startInput",
                    end: "endInput",
                  }}
                  onChange={handleDateFrom}
                />
              </div>
              <div className="col-3 col-md-3">
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Please select site(s)"
                  style={{
                    width: "100%",
                  }}
                  onChange={handleSiteChange}
                  maxTagCount="responsive"
                  maxTagPlaceholder={(omittedValues) => (
                    <Tooltip
                      styles={{
                        root: {
                          pointerEvents: "none",
                        },
                      }}
                      title={omittedValues.map(({ label }) => label).join(", ")}
                    >
                      <span>...</span>
                    </Tooltip>
                  )}
                  options={[
                    {
                      value: "604a288405a8ed2d4d4bba4b",
                      label: "Fab 10A1",
                    },
                    {
                      value: "604a286b05a8ed2d4d4bba4a",
                      label: "Fab 10A2",
                    },
                    {
                      value: "604a28bb05a8ed2d4d4bba4c",
                      label: "Fab 10N",
                    },
                    {
                      value: "60cafd5a4e8783df2ab8afff",
                      label: "Fab 10NX",
                    },
                    {
                      value: "6052a5b7be761a25aaec1bd1",
                      label: "Fab 10W",
                    },
                    {
                      value: "604a28ee05a8ed2d4d4bba4e",
                      label: "Fab 10X",
                    },
                  ]}
                />
              </div>
              <div className="col-3 col-md-3">
                <Button
                  style={{ width: "100%" }}
                  onClick={(e) => {
                    e.preventDefault();
                    handleSubmit();
                  }}
                >
                  Submit
                </Button>
              </div>
            </div>
            <br />
            <div className="row dec-row">
              <div className="col-6 col-md-6">
                <div className="card">
                  <style>{SUMMARY_STICKY_CSS}</style>
                  <Table
                    className="d6-summary-fixed"
                    pagination={false}
                    columns={tableColumns}
                    dataSource={results}
                    loading={loading}
                    rowKey="key"
                    bordered
                    size="small"
                    // No scrollbar: the table renders all months at full height
                    // (Grant Total summary stays pinned at the bottom via CSS).
                    // scroll={{ y: 320 }}
                    summary={() => (
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0}>Grant Total</Table.Summary.Cell>
                        <Table.Summary.Cell index={1}>{grantTotals.generalPermitCount}</Table.Summary.Cell>
                        <Table.Summary.Cell index={2}>{grantTotals.toolInstallPermitCount}</Table.Summary.Cell>
                        <Table.Summary.Cell index={3}>{grantTotals.childPermitCount}</Table.Summary.Cell>
                        <Table.Summary.Cell index={4}>{grantTotals.grantTotal}</Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}
                  />
                </div>
              </div>
              <div className="col-6 col-md-6">
                <div className="card" style={{ minHeight: 360 }}>
                  <h5 className="text-center mb-2">No of E-Permit</h5>
                  <Column {...chartConfig} data={chartData} loading={loading} height={320} autoFit={false} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};
